import { IArcVoiceAudioStats } from '@common/interfaces/arcVoice'

/** Mic level that counts as the caller talking over Arc rather than Arc's own echo, by default. */
export const BARGE_IN_LEVEL = 0.3
/** Consecutive loud chunks (~43ms each) it takes, so one echo spike can't cut Arc off. */
export const BARGE_IN_CHUNKS = 2
/** Chunks held while Arc talks (~340ms), so a caller who talks over it keeps the start of what they said. */
export const HELD_CHUNKS = 8

export interface IEchoGateFrame {
  samples: Float32Array
  /** 0–1, the chunk's level. */
  level: number
  /** Audio-clock seconds. */
  at: number
  /** When Arc's voice stops coming out of the speakers. */
  playbackEndsAt: number
  /** When its echo is gone too: the end of the window the mic is held back in. */
  echoEndsAt: number
}

export interface IEchoGateDecision {
  /** Chunks to send now, oldest first. */
  send: Float32Array[]
  /** The caller is plainly talking over Arc: stop it. */
  isBargeIn: boolean
}

/** Echo levels, counted in hundredths, and the level of each cut-in: enough to tune from. */
export interface IEchoGateTally {
  echoBuckets: number[]
  cutIns: number[]
}

const BUCKETS = 100

export const emptyTally = (): IEchoGateTally => ({
  echoBuckets: Array.from({ length: BUCKETS }, () => 0),
  cutIns: [],
})

export const mergeTallies = (a: IEchoGateTally, b: IEchoGateTally): IEchoGateTally => ({
  echoBuckets: a.echoBuckets.map((count, index) => count + (b.echoBuckets[index] ?? 0)),
  cutIns: [...a.cutIns, ...b.cutIns],
})

// A loud or close mic reads above 1; the record keeps levels on the 0-1 scale.
const round = (value: number) => Math.round(Math.min(1, value) * 100) / 100

/** A call's tally in the shape its record keeps. */
export const summarizeTally = (tally: IEchoGateTally, cutInLevel: number): IArcVoiceAudioStats => {
  const echoChunks = tally.echoBuckets.reduce((sum, count) => sum + count, 0)
  const levelAt = (share: number) => {
    let seen = 0
    const index = tally.echoBuckets.findIndex((count) => (seen += count) >= share * echoChunks)
    return Math.max(0, index) / BUCKETS
  }
  const peakIndex = tally.echoBuckets.reduce((last, count, index) => (count ? index : last), 0)
  const cutIns = [...tally.cutIns].sort((x, y) => x - y)
  return {
    cutInLevel,
    echoChunks,
    echoP95: echoChunks ? levelAt(0.95) : 0,
    echoPeak: echoChunks ? peakIndex / BUCKETS : 0,
    cutIns: cutIns.length,
    cutInMin: cutIns.length ? round(cutIns[0]) : null,
    cutInMedian: cutIns.length ? round(cutIns[Math.floor(cutIns.length / 2)]) : null,
  }
}

/**
 * Decides which mic audio reaches the provider while Arc's voice is playing.
 * Echo cancellation alone still let the model hear itself, so audio from that
 * window is held back rather than sent. It is held, not dropped: when the caller
 * is plainly talking over Arc, everything held goes out with the chunk that
 * proved it, and when Arc simply finishes, what was said after its voice stopped
 * goes out ahead of the next chunk. Either way the start of their words survives.
 */
export class EchoGate {
  private held: Array<{ samples: Float32Array; at: number }> = []

  private loudRun = 0

  private loudestInRun = 0

  /** What the mic heard while Arc talked, for tuning the level. */
  readonly tally: IEchoGateTally = emptyTally()

  /** `level`: the mic level that counts as cutting in — the caller's sensitivity. */
  constructor(private readonly level = BARGE_IN_LEVEL) {}

  decide({ samples, level, at, playbackEndsAt, echoEndsAt }: IEchoGateFrame): IEchoGateDecision {
    if (at >= echoEndsAt) {
      const afterArc = this.held.filter((chunk) => chunk.at >= playbackEndsAt)
      this.reset()
      return { send: [...afterArc.map((chunk) => chunk.samples), samples], isBargeIn: false }
    }

    this.held = [...this.held, { samples, at }].slice(-HELD_CHUNKS)
    const isLoud = level >= this.level
    this.loudRun = isLoud ? this.loudRun + 1 : 0
    this.loudestInRun = isLoud ? Math.max(this.loudestInRun, level) : 0
    if (this.loudRun < BARGE_IN_CHUNKS) {
      this.tally.echoBuckets[Math.min(BUCKETS - 1, Math.floor(level * BUCKETS))] += 1
      return { send: [], isBargeIn: false }
    }

    this.tally.cutIns.push(this.loudestInRun)
    const send = this.held.map((chunk) => chunk.samples)
    this.reset()
    return { send, isBargeIn: true }
  }

  reset(): void {
    this.held = []
    this.loudRun = 0
    this.loudestInRun = 0
  }
}
